import {
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  VStack,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import ArticlesGrid, { ArticleHeader } from '../../../components/ArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isDoctorRecruiterAccount,
  isLicenseVerified,
  isPublisherAccount,
} from '../../../common/libs/user';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import useSpecialPage from '../../../hooks/useSpecialPage';
import FullWidthBox from '../../../components/FullWidthBox';
import {
  generateS3DownloadUrl,
  getSiteSettings,
  Special,
} from '../../../server/libs/siteSettings';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';

const specialPageSlug = 'ncc-rare';
const bannerSettingKey = 'nccRare';
const specialPagePath = '/special/ncc-rare';

type AreaInstitution = {
  area: string;
  institutions: {
    name: string;
  }[];
};

const areaInstitutions: AreaInstitution[] = [
  {
    area: '北海道',
    institutions: [],
  },
  {
    area: '東北',
    institutions: [],
  },
  {
    area: '関東',
    institutions: [
      {
        name: '国立研究開発法人 国立がん研究センター 中央病院 希少がんセンター',
      },
      {
        name: '慶應義塾大学病院 整形外科',
      },
    ],
  },
  {
    area: '中部',
    institutions: [
      {
        name: '静岡県立静岡がんセンター 整形外科',
      },
    ],
  },
  {
    area: '中国',
    institutions: [],
  },
  {
    area: '近畿',
    institutions: [],
  },
  {
    area: '四国',
    institutions: [],
  },
  {
    area: '九州沖縄',
    institutions: [
      {
        name: '佐賀大学医学部附属病院 整形外科',
      },
    ],
  },
];
// PC表示用に左右カラムに分割
const leftColumnArea = areaInstitutions.slice(0, 3);
const rightColumnArea = areaInstitutions.slice(3, areaInstitutions.length);

const AreaInstitutionComponent = ({ area, institutions }: AreaInstitution) => (
  <Box pb={2}>
    <Box py={2} fontWeight="bold" fontSize="sm">
      {area}
    </Box>
    <Box>
      {!!institutions.length
        ? institutions.map(({ name }) => (
            <Box fontSize="xs" key={name}>
              {name}
            </Box>
          ))
        : null}
    </Box>
  </Box>
);

const userAccessHistoryOperation = `special_${specialPageSlug}`; // user_access_histories.operationカラムの値のため合計16文字以内

type PageContext = {
  title: string | null;
  articles: ArticleHeader[];
  isMobile: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isPrivate: boolean;
  mainImagePath: string;
};

const NccRarePage: NextPage<PageContext> = ({
  title,
  articles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
  mainImagePath,
}) => {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle: title ?? '',
    gaLabelKey: specialPageSlug,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white" mb={-16}>
        {breadcrumbs}
        <FullWidthBox
          bg={
            isMobile
              ? 'white'
              : 'linear-gradient(to right, #FFFFFF 50%, #F3F3ED 50%)'
          }
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Flex
            color="white"
            justifyContent="center"
            alignItems="center"
            flexDirection={isMobile ? 'column' : 'row'}
            gap={isMobile ? 0 : 12}
          >
            {!isMobile && (
              <Image
                src={generateS3DownloadUrl(
                  'special_articles/ncc-rare/ncc-rare_logo.png',
                )}
                alt="NCC RARE ロゴ"
                width={289}
                maxW={{ base: '100%', md: '48%' }}
              />
            )}
            <Image
              src={generateS3DownloadUrl(
                'special_articles/ncc-rare/ncc-rare_image.png',
              )}
              alt="イメージ画像"
              maxW={{ base: '100%', md: '654' }}
            />
          </Flex>
        </FullWidthBox>
        <FullWidthBox
          bg={'#F4F9FF'}
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Container>
            <VStack
              gap={8}
              alignItems="center"
              px={{
                base: 0,
                md: 8,
              }}
              pt={{
                base: 6,
                md: 8,
              }}
              pb={{
                base: 4,
                md: 8,
              }}
            >
              <Heading as="h2" fontSize="2xl" fontWeight="bold" color="#1F2288">
                非専門医のための悪性骨軟部腫瘍診療ガイド
              </Heading>
              <Grid
                templateColumns={{
                  base: '58px 1fr',
                  md: '83px 1fr',
                }}
                gridRowGap={2}
                gridColumnGap={{
                  base: 2,
                  md: 4,
                }}
                backgroundColor="white"
                py={{
                  base: 4,
                  md: 8,
                }}
                px={{
                  base: 2,
                  md: 8,
                }}
              >
                <GridItem>
                  <Image
                    src={generateS3DownloadUrl(
                      'special_articles/ncc-rare/ncc-rare_doctor.jpg',
                    )}
                    alt="小林英介 先生"
                    height={{
                      base: 58,
                      md: 83,
                    }}
                    rounded="full"
                  />
                </GridItem>
                <GridItem display="flex" flexDirection="column" gap={2}>
                  <Box
                    fontSize={{
                      base: 'sm',
                      md: 'md',
                    }}
                    fontWeight="bold"
                  >
                    悪性骨軟部腫瘍（肉腫・サルコーマ）が疑われる場合には、速やかに専門医へ紹介することが重要です。本企画では、国立がん研究センター中央病院で研修を受け、悪性骨軟部腫瘍を専門とする医師をご紹介することも目的の一つとしています。
                  </Box>
                  <Flex display="flex" gap={1} flexWrap="wrap" fontSize="sm">
                    <Box>国立研究開発法人国立がん研究センター中央病院</Box>
                    <Box>希少がんセンター</Box>
                    <Box>小林英介 先生</Box>
                  </Flex>
                </GridItem>
              </Grid>
            </VStack>
          </Container>
        </FullWidthBox>
        <Box px={[4, 6, 0]} pb={12}>
          <Flex py={10} flexDirection="column" gap={4}>
            <Box>
              <Box fontWeight="bold">現状の課題</Box>
              悪性骨軟部腫瘍は希少がんの一種であり、専門医による診療が中心となっています。悪性骨軟部腫瘍の診断や治療を遅延なく行うためには、適切な治療機関へ紹介する必要があります。一方、非専門医にとって、どのような患者さんを専門医に紹介すべきかの基準が不明瞭であることや、紹介先が分からないことが課題です。
            </Box>
            <Box>
              <Box fontWeight="bold">本企画の目的</Box>
              非専門医の方々にも、悪性骨軟部腫瘍診療における基本的な診療プロセスと、どのタイミングでどの患者をどの専門機関に紹介すべきかを明確に提示すること、医師同士の連携を強化し、診療の質向上と悪性骨軟部腫瘍の患者ケアの迅速化を図ることを目指しています。
            </Box>
          </Flex>
          <Box my={4} py={8} borderBottom="1px solid" borderColor="gray.200">
            {articles.length > 0 && (
              <Box>
                <Heading py={2} as="h3" fontWeight="bold" fontSize="lg">
                  協力医師からのスライド
                </Heading>
                <ArticlesGrid
                  articles={articles}
                  isMobile={isMobile}
                  gaEventLabel={`${specialPageSlug}_articles`}
                  onShowThumbnail={(articleId) => {
                    sendAnalyticsEvent(
                      'slide_thumbnail',
                      'show',
                      `special_${specialPageSlug}#${articleId}`,
                    );
                  }}
                  onClick={(articleId) => {
                    sendAnalyticsEvent(
                      'slide_thumbnail',
                      'click',
                      `special_${specialPageSlug}#${articleId}`,
                    );
                  }}
                />
              </Box>
            )}
          </Box>
          <Box my={4} py={8}>
            <Box>
              <Heading py={2} as="h3" fontWeight="bold" fontSize="lg">
                骨軟部腫瘍診療を相談できる施設の紹介
              </Heading>
              <Box fontSize="sm">
                国立がん研究センターで骨軟部腫瘍診療に関する研修を受けた医師が参加するグループを運営しています。骨軟部腫瘍を疑う場合などにご相談ください。
              </Box>
            </Box>
            <Flex
              justifyContent="center"
              alignItems="center"
              pt={isMobile ? 4 : 12}
              pb={isMobile ? 4 : 8}
              px={isMobile ? 0 : 4}
            >
              <Image
                src={generateS3DownloadUrl(
                  'special_articles/ncc-rare/ncc-rare_map_202604.png',
                )}
                alt="希少がん診療相談施設の地図"
                maxW={isMobile ? '100%' : 760}
              />
            </Flex>
          </Box>
          <Flex backgroundColor="backgroundLight" px={4} py={2} gap={2}>
            {[leftColumnArea, rightColumnArea].map((areas, index) => (
              <Box key={index} w={isMobile ? '100%' : '50%'}>
                {areas.map(({ area, institutions }) => (
                  <AreaInstitutionComponent
                    key={area}
                    area={area}
                    institutions={institutions}
                  />
                ))}
              </Box>
            ))}
          </Flex>
        </Box>
      </Box>
    </>
  );
};

export default NccRarePage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { isPrivate, canDisplay, title } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    // 掲載スライドのarticleIdsの値とOGP画像のパスで使用するmainImagePathのみS3のサイト設定のバナー設定値から取得
    let mainImagePath: string = '';
    let articleIds: string[] = [];
    let siteSettings;
    try {
      siteSettings = await getSiteSettings();
    } catch (e) {
      console.error('サイト設定の取得に失敗しました。', e);
      return {
        notFound: true,
      };
    }
    const setting: Special | undefined =
      siteSettings?.bannerSettings?.banners?.[bannerSettingKey] ?? undefined;
    if (setting?.articleIds) {
      articleIds = setting.articleIds;
    }
    if (setting) {
      mainImagePath =
        setting?.mainImage ??
        (setting?.mainImageS3Key
          ? generateS3DownloadUrl(setting.mainImageS3Key)
          : '');
    }

    let articles: ArticleHeader[] = [];
    const articleHeaders = await prisma.article_header.findMany({
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: articleIds,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
      orderBy: {
        create_date: 'desc',
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    // アカウント統合済みのユーザのプロフィールのみ返却される
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    articles = articleHeaders.reduce((articles: any, article: any) => {
      const author = article.user_article_header_author_idTouser;
      const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

      if (
        shouldNotShow(
          idBaseProfile,
          context.req.user,
          context.req.isAuthenticated(),
          article,
        )
      ) {
        return articles;
      }

      articles.push({
        articleId: article.article_id,
        name: article.name,
        articleType: article.article_type,
        status: article.status as ArticleStatus | null,
        topImageM: article.top_img_m,
        sessionCount: article.session_count || 0,
        likeCount:
          article.like_count +
          article.article_page.reduce(
            (prev: any, curr: any) => prev + curr.like_count,
            0,
          ),
        commentCount: article.article_comments.length,
        discloseScope: article.disclose_scope as DiscloseScope | null,
        createDate: date.toIso8601Jst(article.create_date),
        updateDate: date.toIso8601Jst(article.update_date),
        author: {
          userId: article.user_article_header_author_idTouser.user_id,
          userName: idBaseProfile?.userName ?? null,
          // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
          lastName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.lastName ?? author.last_name,
          firstName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.firstName ?? author.first_name,
          updateDateEpochMilliseconds:
            article.user_article_header_author_idTouser.update_date.getTime(),
          profilePhotoS3Key:
            article.user_article_header_author_idTouser.profile_photo_s3_key,
          idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
          antaaAccountType: idBaseProfile?.accountType ?? null,
          antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
          isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
        },
        tags: article.article_tag_connection.map((conn: any) => ({
          id: conn.tag.tag_id,
          name: conn.tag.tag_name,
        })),
      });
      return articles;
    }, []);

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        title,
        articles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
        mainImagePath,
      },
    };
  },
);
